import React, { useCallback, useEffect, useState } from 'react';
import type { AdminOverviewRow, ClientDto, UserDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon } from '../icons';
import { euro, hm, thisMonth } from '../format';
import { Avatar, Empty, ErrorBanner, MonthNav, Panel, Pill, Row, Stat } from '../ui';
import { UserActions, UserForm, UserRow } from './UsersManager';

/** Commerciali: accessi, clienti assegnati (segnalazione/gestione) e provvigioni del mese. */
export function AgentsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [agents, setAgents] = useState<UserDto[]>([]);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [overview, setOverview] = useState<AdminOverviewRow[]>([]);
  const [month, setMonth] = useState(thisMonth());
  const [selectedId, setSelectedId] = useNavState<number | null>(ctx, 'agent', null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [a, c] = await Promise.all([api.get<UserDto[]>('/api/admin/users?role=agent'), api.get<ClientDto[]>('/api/admin/clients')]);
    setAgents(a);
    setClients(c);
  }, [api]);

  const loadOverview = useCallback(async () => {
    const r = await api.get<{ month: string; rows: AdminOverviewRow[] }>(`/api/admin/overview?month=${month}`);
    setOverview(r.rows);
  }, [api, month]);

  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);
  useEffect(() => {
    loadOverview().catch((e) => setError((e as Error).message));
  }, [loadOverview]);

  const agent = agents.find((a) => a.id === selectedId) ?? null;
  const assigned = agent ? clients.filter((c) => c.referrerUserId === agent.id || c.managerUserId === agent.id) : [];
  const commissionFor = (c: ClientDto) => {
    const r = overview.find((o) => o.clientId === c.id);
    if (!r || !agent) return 0;
    return (c.referrerUserId === agent.id ? r.referralCents : 0) + (c.managerUserId === agent.id ? r.managementCents : 0);
  };
  const total = assigned.reduce((a, c) => a + commissionFor(c), 0);

  return (
    <div className="panels">
      <Panel title="Commerciali" subtitle="Chi segnala o gestisce i clienti">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {agents.length === 0 ? (
          <Empty icon="users">Nessun commerciale. Aggiungilo qui sotto.</Empty>
        ) : (
          agents.map((a) => (
            <UserRow
              key={a.id}
              user={a}
              selected={selectedId === a.id}
              onSelect={() => setSelectedId(a.id)}
              right={<Pill icon="building" title="Clienti assegnati">{clients.filter((c) => c.referrerUserId === a.id || c.managerUserId === a.id).length}</Pill>}
            />
          ))
        )}
        <UserForm api={api} role="agent" onAdded={load} label="Nuovo commerciale" />
      </Panel>

      <Panel title={agent ? agent.name : 'Scheda commerciale'} subtitle={agent?.email}>
        {!agent ? (
          <Empty icon="user">Seleziona un commerciale per vedere clienti assegnati e provvigioni.</Empty>
        ) : (
          <>
            <div className="card hstack">
              <Avatar name={agent.name} navy size={48} />
              <div className="grow">
                <div style={{ fontSize: 16, fontWeight: 500 }}>{agent.name}</div>
                <div className="muted small">{agent.active ? 'Accesso attivo' : 'Accesso disattivato'}</div>
              </div>
              <Pill tone={agent.active ? 'good' : 'bad'} icon={agent.active ? 'checkCircle' : 'x'}>
                {agent.active ? 'attivo' : 'disattivato'}
              </Pill>
            </div>
            <UserActions api={api} user={agent} onChanged={load} />

            <MonthNav value={month} onChange={setMonth} />
            <div className="stats">
              <Stat value={assigned.length} label="clienti assegnati" />
              <Stat value={euro(total)} label="provvigioni del mese" navy />
            </div>

            <div className="eyebrow">Clienti assegnati</div>
            {assigned.length === 0 ? (
              <Empty icon="building">Nessun cliente: si assegna dalla scheda del cliente («Segnalato da» o «Gestito da»).</Empty>
            ) : (
              assigned.map((c) => {
                const r = overview.find((o) => o.clientId === c.id);
                return (
                  <Row key={c.id}>
                    <Avatar name={c.name} on={r?.online} />
                    <div className="grow">
                      <div className="title">{c.name}</div>
                      <div className="sub hstack" style={{ gap: 6 }}>
                        {c.referrerUserId === agent.id && <Pill tone="info" icon="star">segnalazione {c.referralPct}%</Pill>}
                        {c.managerUserId === agent.id && <Pill tone="warn" icon="shield">gestione {c.managementPct}%</Pill>}
                        {r && <span>{hm(r.minutes)} lavorate</span>}
                      </div>
                    </div>
                    <span className="pill">{euro(commissionFor(c))}</span>
                    <button type="button" className="btn icon small ghost" title="Ore e importi del mese di questo cliente" onClick={() => ctx.setPage('reports', { clientId: c.id })}>
                      <Icon name="list" size={16} />
                    </button>
                  </Row>
                );
              })
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
